import React from "react";
import { CheckCircle2, CircleAlert } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";

import {
  CAMPAIGN_TYPE_LABELS,
  CampaignType,
  IPrizeRow,
  VOUCHER_VALIDITY_MODE_LABELS,
  VoucherValidityMode,
} from "../../../../../@types/gamification";
import { PriorityBadge } from "../../components/priority-badge";
import { ProbabilityPie } from "../../components/probability-pie";
import { formPriorityLevelOf } from "../../constants/priority-levels";
import {
  BANNER_GALLERY_COPY,
  CAMPAIGN_LINK_COPY,
  CUSTOMER_FACING_COPY,
} from "../../constants/messages";
import type { CreateGamificationFormData } from "../../validations/gamification.validation";

const COPY = {
  TITLE: "Revisão",
  DESCRIPTION: "Confira os dados antes de salvar a campanha.",
  ALL_STORES: "Todas as lojas",
  NO_FILE: "Nenhum arquivo",
  KEPT_FILE: "Arquivo atual mantido",
  CHECKLIST_TITLE: "Checklist de publicação",
  CHECKLIST_OK: "Tudo certo — a campanha pode ser publicada.",
  CHECKLIST_PENDING:
    "Resolva as pendências abaixo para liberar a publicação:",
  PARALLEL: "Paralela (não concorre)",
} as const;

type ReviewValues = Partial<CreateGamificationFormData>;

interface ReviewStepProps {
  values: ReviewValues;

  pendingIssues: string[];
  banner3x4Preview: string | null;
  banner16x9Preview: string | null;

  hasExistingBanner3x4?: boolean;
  hasExistingBanner16x9?: boolean;
  hasExistingRegulation?: boolean;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-2">
    <span className="text-sm font-medium text-muted-foreground sm:w-56 sm:shrink-0">
      {label}
    </span>
    <span className="text-sm">{children}</span>
  </div>
);

function storesSummary(values: ReviewValues): string {
  if (values.allStoresSelected) return COPY.ALL_STORES;
  const stores = values.stores ?? [];
  if (stores.length === 0) return "—";
  const codes = stores.map((store) => store.storeCode).join(", ");
  return `${stores.length} loja(s): ${codes}`;
}

function fileSummary(
  file: File | undefined,
  hasExisting: boolean | undefined,
): string {
  if (file instanceof File) return file.name;
  if (hasExisting) return COPY.KEPT_FILE;
  return COPY.NO_FILE;
}

function bannerSummary(
  file: File | undefined,
  galleryLabel: string | undefined,
  hasExisting: boolean | undefined,
): string {
  if (file instanceof File) return file.name;
  if (galleryLabel) return BANNER_GALLERY_COPY.selected(galleryLabel);
  return fileSummary(file, hasExisting);
}

function orDefault(value: unknown, fallback: string): string {
  if (value === "" || value == null) return fallback;
  return String(value);
}

type ValiditySummaryInput =
  | {
      mode?: VoucherValidityMode;
      validityDays?: number;
      startOffsetDays?: number;
    }
  | undefined;

function validitySummary(validity: ValiditySummaryInput): string {
  const mode = validity?.mode ?? VoucherValidityMode.DAYS;
  const byMode: Record<VoucherValidityMode, () => string> = {
    [VoucherValidityMode.DAYS]: () => {
      const days = validity?.validityDays;
      if (!days) return VOUCHER_VALIDITY_MODE_LABELS.DAYS;
      return `${days} dias após a emissão`;
    },
    [VoucherValidityMode.END_OF_NEXT_MONTH]: () =>
      VOUCHER_VALIDITY_MODE_LABELS.END_OF_NEXT_MONTH,
    [VoucherValidityMode.CAMPAIGN_END]: () =>
      VOUCHER_VALIDITY_MODE_LABELS.CAMPAIGN_END,
  };
  const base = byMode[mode]();
  const startOffset = Number(validity?.startOffsetDays ?? 0);
  if (startOffset > 0) {
    return `${base} · início ${startOffset} dia(s) após a emissão`;
  }
  return base;
}

function concurrencySummary(values: ReviewValues): string {
  const concurrency = values.mechanicConfig?.concurrency;
  if (!concurrency?.competes) return COPY.PARALLEL;
  return `Concorre no grupo "${concurrency.group ?? "—"}"`;
}

function segmentationSummary(values: ReviewValues): string {
  if (!values.mechanicConfig?.cpfSegmentation?.enabled) return "Não";

  if (values.segmentationCpfs === undefined) return "Sim";
  const count = values.segmentationCpfs.length;
  if (count > 0) return `Sim — ${count} CPF(s)`;
  return "Sim — base pendente";
}

function faqSummary(values: ReviewValues): React.ReactNode {
  const faqItems = values.faqItems ?? [];
  if (faqItems.length === 0) {
    return "FAQ padrão da campanha";
  }

  return (
    <div className="space-y-2">
      <p>{faqItems.length} pergunta(s) personalizada(s)</p>
      <ul className="space-y-1 rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        {faqItems.map((item, index) => (
          <li key={`${item.question}-${index}`}>
            <strong className="text-foreground">{index + 1}.</strong>{" "}
            {item.question}
          </li>
        ))}
      </ul>
    </div>
  );
}

const ChecklistCard: React.FC<{ issues: string[] }> = ({ issues }) => (
  <Card>
    <CardHeader>
      <CardTitle>{COPY.CHECKLIST_TITLE}</CardTitle>
    </CardHeader>
    <CardContent>
      {issues.length === 0 && (
        <p className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {COPY.CHECKLIST_OK}
        </p>
      )}
      {issues.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {COPY.CHECKLIST_PENDING}
          </p>
          <ul className="space-y-1.5">
            {issues.map((issue) => (
              <li
                key={issue}
                className="flex items-center gap-2 text-sm text-red-600"
              >
                <CircleAlert className="h-4 w-4 shrink-0" />
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}
    </CardContent>
  </Card>
);

const PrizesSummaryCard: React.FC<{ prizes: Array<Partial<IPrizeRow>> }> = ({
  prizes,
}) => {
  const total = prizes.reduce(
    (sum, prize) => sum + (Number(prize?.probability) || 0),
    0,
  );
  const totalColor =
    Math.abs(total - 100) < 0.01 ? "text-green-600" : "text-red-600";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Prêmios</CardTitle>
        <span className={`text-sm font-medium ${totalColor}`}>
          Total: {total.toFixed(2)}%
        </span>
      </CardHeader>
      <CardContent>
        {prizes.length === 0 && (
          <p className="text-sm text-gray-500">Nenhum prêmio configurado.</p>
        )}
        {prizes.length > 0 && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_220px]">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-1.5 pr-4">#</th>
                  <th className="py-1.5 pr-4">Valor (R$)</th>
                  <th className="py-1.5">Porcentagem (%)</th>
                </tr>
              </thead>
              <tbody>
                {prizes.map((prize, index) => (
                  <tr key={index} className="border-t">
                    <td className="py-1.5 pr-4 text-gray-500">{index + 1}</td>
                    <td className="py-1.5 pr-4">
                      {Number(prize?.value) || 0}
                    </td>
                    <td className="py-1.5">
                      {Number(prize?.probability) || 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rounded-lg border bg-gray-50/60 p-4">
              <ProbabilityPie prizes={prizes} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function MechanicParamsRows({ values }: { values: ReviewValues }) {
  const mechanic = values.campaignType ?? CampaignType.ROULETTE;
  const params = values.mechanicConfig?.mechanicParams;

  const byMechanic: Record<CampaignType, React.ReactNode> = {
    [CampaignType.ROULETTE]: null,
    [CampaignType.SCRATCH]: null,
    [CampaignType.PENALTY]: (
      <Row label="Probabilidade de gol">
        {orDefault(params?.goalProbability, "—")}
      </Row>
    ),
    [CampaignType.SLOT]: (
      <Row label="Símbolos">
        {(params?.symbols ?? [])
          .map((symbol) => `${symbol.label} (R$ ${symbol.value})`)
          .join(" · ") || "—"}
      </Row>
    ),
  };
  return <>{byMechanic[mechanic]}</>;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  values,
  pendingIssues,
  banner3x4Preview,
  banner16x9Preview,
  hasExistingBanner3x4,
  hasExistingBanner16x9,
  hasExistingRegulation,
}) => {
  const config = values.mechanicConfig;

  return (
    <div className="space-y-6">
      <ChecklistCard issues={pendingIssues} />

      <Card>
        <CardHeader>
          <CardTitle>{COPY.TITLE}</CardTitle>
          <p className="text-sm text-muted-foreground">{COPY.DESCRIPTION}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Row label="Mecânica">
            {CAMPAIGN_TYPE_LABELS[values.campaignType ?? CampaignType.ROULETTE]}
          </Row>
          <Row label="Nome">{values.campaignName || "—"}</Row>
          <Row label={CAMPAIGN_LINK_COPY.reviewLabel}>
            {values.slug?.trim() || "—"}
          </Row>
          <Row label={CUSTOMER_FACING_COPY.nameLabel}>
            {values.mechanicConfig?.presentation?.customerFacingName?.trim() ||
              "—"}
          </Row>
          <Row label={CUSTOMER_FACING_COPY.descriptionLabel}>
            {values.mechanicConfig?.presentation?.customerFacingDescription?.trim() ||
              "—"}
          </Row>
          <Row label="Período">
            {values.startDate || "—"} até {values.endDate || "—"}
          </Row>
          <Row label="Lojas">{storesSummary(values)}</Row>
        </CardContent>
      </Card>

      <PrizesSummaryCard prizes={config?.prizes ?? []} />

      <Card>
        <CardHeader>
          <CardTitle>Configuração</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Row label="Jogadas por CPF">
            {orDefault(config?.maxPlays, "padrão (3)")}
          </Row>
          <Row label="Compra mínima (R$)">
            {orDefault(config?.minPurchase, "padrão")}
          </Row>
          <Row label="Prioridade">
            <PriorityBadge
              value={formPriorityLevelOf(config?.priority)}
              showPrefix={false}
            />
          </Row>
          <Row label="Concorrência">{concurrencySummary(values)}</Row>
          <Row label="Validade do voucher">
            {validitySummary(config?.voucherValidity)}
          </Row>
          <Row label="Segmentação por CPF">{segmentationSummary(values)}</Row>
          <MechanicParamsRows values={values} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>FAQ</CardTitle>
        </CardHeader>
        <CardContent>
          <Row label="Fonte do FAQ">{faqSummary(values)}</Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Arquivos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Row label="Regulamento">
            {fileSummary(values.regulations, hasExistingRegulation)}
          </Row>
          <Row label={CUSTOMER_FACING_COPY.coverLabel}>
            {bannerSummary(
              values.banner3x4,
              values.banner3x4SourcePreview?.label,
              hasExistingBanner3x4,
            )}
          </Row>
          <Row label={CUSTOMER_FACING_COPY.alternativeLabel}>
            {bannerSummary(
              values.banner16x9,
              values.banner16x9SourcePreview?.label,
              hasExistingBanner16x9,
            )}
          </Row>

          {(banner3x4Preview || banner16x9Preview) && (
            <div className="flex flex-wrap gap-4 pt-2">
              {banner3x4Preview && (
                <img
                  src={banner3x4Preview}
                  alt="Prévia da capa do card na home"
                  className="h-32 rounded-md border object-cover"
                />
              )}
              {banner16x9Preview && (
                <img
                  src={banner16x9Preview}
                  alt="Prévia do formato largo"
                  className="h-32 rounded-md border object-cover"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
